import { useEffect, useState } from "react";
import SilhouetteModel, {
  type Gender,
  type HeightValue,
} from "./onboarding/SilhouetteModel";

export type LookbookModelProfile = {
  model_gender?: string | null;
  model_height?: string | null;
  model_body_shape?: string | null;
  model_body_size?: string | null;
  model_skin_tone?: string | null;
  model_hair?: string | null;
} | null;

export type LookbookLoadingSilhouetteProps = {
  modelProfile: LookbookModelProfile;
  label?: string;
  messages: string[];
  className?: string;
};

function normaliseGender(g: string | null | undefined): Gender | null {
  if (!g) return null;
  if (g === "man" || g === "men" || g === "male") return "men";
  if (g === "woman" || g === "women" || g === "female") return "women";
  return "non-binary";
}

function normaliseHeight(h: string | null | undefined): HeightValue | null {
  if (!h) return null;
  const allowed: HeightValue[] = [
    "short",
    "average_short",
    "average_tall",
    "tall",
    "average",
  ];
  return (allowed as string[]).includes(h) ? (h as HeightValue) : "average";
}

export function LookbookLoadingSilhouette({
  modelProfile,
  label = "AI generating",
  messages,
  className,
}: LookbookLoadingSilhouetteProps) {
  const safeMessages = messages.length > 0 ? messages : ["Generating…"];
  const [msgIndex, setMsgIndex] = useState(0);
  const [msgOpacity, setMsgOpacity] = useState(1);

  useEffect(() => {
    if (safeMessages.length <= 1) return;
    const interval = setInterval(() => {
      setMsgOpacity(0);
      setTimeout(() => {
        setMsgIndex((i) => (i + 1) % safeMessages.length);
        setMsgOpacity(1);
      }, 300);
    }, 4000);
    return () => clearInterval(interval);
  }, [safeMessages.length]);

  const gender = normaliseGender(modelProfile?.model_gender);
  const height = normaliseHeight(modelProfile?.model_height);

  return (
    <div
      className={className}
      style={{
        position: "relative",
        width: "100%",
        aspectRatio: "3 / 4",
        background: "var(--secondary, #F5F1EC)",
        borderRadius: "var(--radius, 16px)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <style>{`
        @keyframes l2fSilhouettePulse {
          0% { opacity: 0.55; }
          50% { opacity: 1; }
          100% { opacity: 0.55; }
        }
        @keyframes l2fProgressFill {
          0% { width: 0%; }
          100% { width: 90%; }
        }
        .l2f-silhouette-pulse {
          animation: l2fSilhouettePulse 2s ease-in-out infinite;
        }
        .l2f-silhouette-progress {
          animation: l2fProgressFill 45s linear forwards;
        }
      `}</style>

      {/* Label pill */}
      {label && (
        <span
          style={{
            position: "absolute",
            top: 12,
            left: 12,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            padding: "4px 10px",
            borderRadius: 9999,
            background: "var(--l2f-cream)",
            color: "var(--l2f-coral)",
            zIndex: 2,
          }}
        >
          {label}
        </span>
      )}

      {/* Silhouette */}
      <div
        className="l2f-silhouette-pulse"
        style={{
          height: "60%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: 12,
        }}
      >
        <SilhouetteModel
          gender={gender}
          height={height}
          bodyShape={modelProfile?.model_body_shape ?? null}
          bodySize={modelProfile?.model_body_size ?? null}
          skinTone={modelProfile?.model_skin_tone ?? null}
          hair={modelProfile?.model_hair ?? null}
          isReveal={false}
        />
      </div>

      {/* Rotating message */}
      <div
        style={{
          position: "absolute",
          bottom: 28,
          left: 0,
          right: 0,
          textAlign: "center",
          padding: "0 16px",
          fontFamily: '"Work Sans", system-ui, sans-serif',
          fontSize: 13,
          color: "var(--muted-foreground)",
          opacity: msgOpacity,
          transition: "opacity 0.3s ease",
        }}
      >
        {safeMessages[msgIndex]}
      </div>

      {/* Progress bar */}
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          height: 3,
          background: "rgba(255, 92, 92, 0.12)",
        }}
      >
        <div
          className="l2f-silhouette-progress"
          style={{
            height: "100%",
            background: "var(--l2f-coral)",
            width: "0%",
          }}
        />
      </div>
    </div>
  );
}

export default LookbookLoadingSilhouette;
